// LEADERSHIP DERAILERS ASSESSMENT — Scoring Engine
// Scale: 0-4 per item. Curvilinear interpretation (Kaiser & Hogan, 2011):
// moderate levels are often functional; only elevated levels predict derailment.
import { LD_ITEMS, LD_DIMS } from "./questionBank.js";

export const LD_NORMS = {
  overall: { mean:46.2, sd:14.8, n:720 },
};

export const LD_DIM_BANDS = [
  { min:0,  max:40, label:"Low",      level:"low",
    color:"#059669", bg:"#f0fdf4", border:"#86efac", icon:"🟢",
    note:"This trait is not a notable feature of this profile, neither a strength nor a risk in this area." },
  { min:40, max:70, label:"Functional Range", level:"moderate",
    color:"#1d4ed8", bg:"#eff6ff", border:"#bfdbfe", icon:"🔵",
    note:"Present at a level that is typically adaptive rather than derailing, often a genuine asset when channelled well." },
  { min:70, max:101,label:"Elevated - Risk Zone", level:"elevated",
    color:"#dc2626", bg:"#fef2f2", border:"#fecaca", icon:"🔴",
    note:"Present at a level research associates with derailment risk, particularly under sustained pressure or fatigue." },
];

export const LD_BANDS = [
  { min:0,  max:35, label:"Low Overall Risk", level:"low",
    color:"#059669", bg:"#f0fdf4", border:"#86efac", icon:"🟢",
    desc:"No dimension shows elevated derailment risk. This is a well-regulated profile under the pressure conditions the assessment describes." },
  { min:35, max:55, label:"Moderate Overall Risk", level:"moderate",
    color:"#d97706", bg:"#fffbeb", border:"#fde68a", icon:"🟡",
    desc:"One or more dimensions show functional-range elevation. Worth monitoring, particularly during periods of sustained stress, but not an immediate concern." },
  { min:55, max:75, label:"Elevated Risk", level:"elevated",
    color:"#dc2626", bg:"#fef2f2", border:"#fecaca", icon:"🔴",
    desc:"One or more dimensions are in the elevated risk zone. Targeted coaching on the specific dimensions flagged below is recommended, particularly for roles involving sustained high pressure." },
  { min:75, max:101,label:"High Overall Risk", level:"high",
    color:"#991b1b", bg:"#fef2f2", border:"#fca5a5", icon:"⛔",
    desc:"Multiple dimensions show elevated risk. Research on leadership derailment strongly recommends structured coaching support and close monitoring, particularly in high-stakes or high-pressure assignments." },
];

export const LD_INTERACTIONS = [
  { pair:["VOL","ARR"], label:"Volatile + Arrogant",
    desc:"Emotional reactivity combined with resistance to feedback produces a leader who is both unpredictable and unreachable when challenged, a combination strongly linked to abrasive leadership reputations." },
  { pair:["SKP","DET"], label:"Skeptical + Detached",
    desc:"Distrust combined with emotional withdrawal can isolate a leader from the very relationships that would otherwise surface early warning signs of team or project problems." },
  { pair:["CAU","PER"], label:"Cautious + Perfectionistic",
    desc:"Risk-aversion combined with rigid standards produces chronic bottlenecks: decisions delayed by both fear of the wrong call and refusal to accept good-enough work." },
  { pair:["IMP","ARR"], label:"Impulsive + Arrogant",
    desc:"Poor risk calibration combined with overconfidence and resistance to feedback is one of the most consistently cited combinations in analyses of major strategic derailment events." },
  { pair:["DRA","VOL"], label:"Dramatic + Volatile",
    desc:"Attention-seeking combined with visible emotional reactivity can make team dynamics feel unpredictable and centred on managing the leader's emotional state." },
];

export function scoreLD(answers) {
  const dimScores = {};
  const dimCounts = {};
  Object.keys(LD_DIMS).forEach(d => { dimScores[d]=0; dimCounts[d]=0; });

  LD_ITEMS.forEach((item, i) => {
    const raw = answers[i];
    if (raw === null || raw === undefined) return;
    dimScores[item.dim] += Number(raw);
    dimCounts[item.dim]++;
  });

  const dimResults = {};
  Object.keys(LD_DIMS).forEach(dim => {
    const raw = dimScores[dim];
    const count = dimCounts[dim];
    const max = count * 4;
    const pct = max > 0 ? Math.round((raw / max) * 100) : 0;
    const band = LD_DIM_BANDS.find(b => pct >= b.min && pct < b.max) || LD_DIM_BANDS[0];
    dimResults[dim] = { raw, max, pct, level: band.level, band };
  });

  const sorted = Object.values(dimResults).sort((a,b) => b.pct - a.pct);
  const top3Avg = sorted.slice(0,3).reduce((s,d) => s+d.pct, 0) / 3;
  const overallPct = Math.round(top3Avg * 0.6 + (sorted.reduce((s,d)=>s+d.pct,0)/sorted.length) * 0.4);

  const band = LD_BANDS.find(b => overallPct >= b.min && overallPct < b.max) || LD_BANDS[0];

  const elevatedDims = Object.entries(dimResults)
    .filter(([id,r]) => r.level === "elevated")
    .map(([id,r]) => ({ id, ...r, ...LD_DIMS[id] }))
    .sort((a,b) => b.pct - a.pct);

  const activeInteractions = LD_INTERACTIONS.filter(inter =>
    inter.pair.every(d => dimResults[d] && dimResults[d].level === "elevated")
  );

  const norm = LD_NORMS.overall;
  const z = (overallPct - norm.mean) / norm.sd;
  const percentile = Math.min(99, Math.max(1, Math.round(50 + z * 17)));

  return {
    dimResults,
    overallPct, band, percentile,
    elevatedDims,
    interactions: activeInteractions,
    scales: { ld: overallPct },
    perfAvg: (100-overallPct)/10, potAvg: (100-overallPct)/10,
    perfBox: overallPct <= 35 ? 3 : overallPct <= 55 ? 2 : 1,
    potBox: overallPct <= 35 ? 3 : overallPct <= 55 ? 2 : 1,
  };
}
